import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import "./ContentStyles.css";
import {
  FaArrowRight,
  FaCheckCircle,
  FaClock,
  FaCrown,
  FaLayerGroup,
  FaLockOpen,
  FaPlay,
  FaStar,
  FaVideo,
  FaWhatsapp,
  FaPenNib,
  FaHeadphones,
  FaMicrophoneAlt,
  FaClipboardCheck,
  FaLightbulb,
} from "react-icons/fa";

import type { CourseDetail, CourseLevelKey } from "./CoursesDetails";

type ContentLesson = {
  id: number;
  title: string;
  subtitle: string;
  duration: string;
  videoUrl: string;
  posterUrl: string;
  description: string;
  focus: string;
  practice: string[];
  homework: string[];
};

type ContentProps = {
  courseLevel: CourseLevelKey;
  course: CourseDetail;
  studentName?: string;
  onLogout?: () => void;
};

const createLessons = (
  level: CourseLevelKey,
  titles: string[],
  descriptions: string[],
  focuses: string[],
): ContentLesson[] =>
  titles.map((title, index) => {
    const lessonNumber = index + 1;
    const paddedLesson = String(lessonNumber).padStart(2, "0");
    const lowerLevel = level.toLowerCase();

    return {
      id: lessonNumber,
      title,
      subtitle: `${level}.${lessonNumber} Video-Lektion`,
      duration:
        lessonNumber <= 3 ? "18 Min." : lessonNumber <= 7 ? "24 Min." : "30 Min.",
      videoUrl: `/videos/${lowerLevel}/lesson-${paddedLesson}.mp4`,
      posterUrl: `/videos/${lowerLevel}/poster-${paddedLesson}.jpg`,
      description: descriptions[index],
      focus: focuses[index],
      practice: [
        "Video vollständig ansehen",
        "Neue Wörter laut wiederholen",
        "Beispielsätze selbst sprechen",
        "Kurze Notizen zur Lektion schreiben",
      ],
      homework: [
        "5 eigene Sätze schreiben",
        "2 Minuten laut sprechen üben",
        "Wichtige Wörter wiederholen",
      ],
    };
  });

const contentLessonsMap: Record<CourseLevelKey, ContentLesson[]> = {
  A1: createLessons(
    "A1",
    [
      "Begrüßung & Vorstellung",
      "Alphabet & Aussprache",
      "Zahlen, Uhrzeit & Datum",
      "Familie & Personen",
      "Essen, Trinken & Einkaufen",
      "Wohnen & Alltag",
      "Verben im Präsens",
      "Fragen stellen",
      "Kurze Dialoge",
      "A1 Wiederholung",
    ],
    [
      "Lerne, wie du dich auf Deutsch begrüßt und einfach vorstellst.",
      "Trainiere deutsche Buchstaben, Laute und eine klare Aussprache.",
      "Übe Zahlen, Uhrzeit, Wochentage und einfache Termine.",
      "Sprich über Familie, Freunde und wichtige Personen.",
      "Lerne Wörter und Sätze für Essen, Trinken und Einkaufen.",
      "Beschreibe Wohnung, Zimmer und deinen Alltag.",
      "Verstehe einfache Verben im Präsens und bilde erste Sätze.",
      "Lerne wichtige W-Fragen und Ja/Nein-Fragen.",
      "Trainiere einfache Gespräche für echte Alltagssituationen.",
      "Wiederhole alle wichtigen A1-Themen Schritt für Schritt.",
    ],
    [
      "Sich vorstellen und einfache persönliche Informationen sagen.",
      "Buchstaben erkennen und Wörter sauber aussprechen.",
      "Zahlen, Zeiten und einfache Daten sicher verwenden.",
      "Über Familie und Personen sprechen.",
      "Bestellen, kaufen und einfache Wünsche sagen.",
      "Wohnung und Alltag einfach beschreiben.",
      "Einfache Sätze im Präsens bilden.",
      "Fragen verstehen und richtig antworten.",
      "Kurze Gespräche im Alltag führen.",
      "Alles Gelernte sicher wiederholen.",
    ],
  ),

  A2: createLessons(
    "A2",
    [
      "Alltag sicher beschreiben",
      "Vergangenheit: Perfekt",
      "Termine & Pläne",
      "Gesundheit & Arztbesuch",
      "Arbeit & Beruf",
      "Reisen & Orientierung",
      "Meinungen ausdrücken",
      "Nebensätze mit weil",
      "E-Mails schreiben",
      "A2 Wiederholung",
    ],
    [
      "Erzähle sicherer über deinen Alltag und deine Routinen.",
      "Lerne, wie du über vergangene Ereignisse sprichst.",
      "Plane Termine und erkläre deine Pläne auf Deutsch.",
      "Übe wichtige Sätze für Arzt, Apotheke und Gesundheit.",
      "Sprich über Arbeit, Beruf und einfache Aufgaben.",
      "Trainiere Wegbeschreibungen, Reisen und Orientierung.",
      "Sage deine Meinung mit einfachen und klaren Sätzen.",
      "Baue längere Sätze mit weil und einfachen Nebensätzen.",
      "Schreibe kurze, höfliche E-Mails für Alltag und Arbeit.",
      "Wiederhole alle wichtigen A2-Themen mit Übungen.",
    ],
    [
      "Alltag und Routinen mit mehr Sicherheit beschreiben.",
      "Über gestern, letzte Woche und frühere Erlebnisse sprechen.",
      "Termine vereinbaren und Pläne erklären.",
      "Beim Arzt einfache Beschwerden beschreiben.",
      "Über Beruf, Aufgaben und Arbeitsalltag sprechen.",
      "Wege erklären und Informationen unterwegs verstehen.",
      "Eigene Meinung einfach und klar sagen.",
      "Sätze logisch mit Begründung verbinden.",
      "Kurze E-Mails höflich formulieren.",
      "A2-Themen sicher wiederholen.",
    ],
  ),

  B1: createLessons(
    "B1",
    [
      "Über Erfahrungen sprechen",
      "Meinung & Begründung",
      "Arbeit, Bewerbung & Alltag",
      "Längere Texte verstehen",
      "Diskussionen führen",
      "Konjunktiv II Grundlagen",
      "Präsentationen vorbereiten",
      "Beschwerden formulieren",
      "B1 Schreibtraining",
      "B1 Wiederholung",
    ],
    [
      "Sprich über Erfahrungen, Wünsche und persönliche Erlebnisse.",
      "Formuliere deine Meinung und begründe sie verständlich.",
      "Trainiere wichtige Sprache für Arbeit und Bewerbung.",
      "Lerne Strategien zum Verstehen längerer Texte.",
      "Diskutiere über vertraute Themen mit mehr Sicherheit.",
      "Nutze höfliche Formen mit würde, könnte und hätte.",
      "Bereite kurze Präsentationen klar und strukturiert vor.",
      "Formuliere Beschwerden höflich und effektiv.",
      "Trainiere Briefe, E-Mails und kurze Texte auf B1-Niveau.",
      "Wiederhole Grammatik, Wortschatz und Kommunikation.",
    ],
    [
      "Persönliche Erlebnisse flüssiger erzählen.",
      "Meinungen klar strukturieren und begründen.",
      "Sicherer über Arbeit und Bewerbung sprechen.",
      "Texte schneller verstehen und wichtige Informationen finden.",
      "In Gesprächen aktiv reagieren und nachfragen.",
      "Höfliche Wünsche und Vorschläge formulieren.",
      "Präsentationen verständlich aufbauen.",
      "Probleme höflich und klar erklären.",
      "Texte mit besserer Struktur schreiben.",
      "B1-Inhalte sicher festigen.",
    ],
  ),

  B2: createLessons(
    "B2",
    [
      "Komplexe Texte verstehen",
      "Argumentieren & Diskutieren",
      "Berufliche Kommunikation",
      "Grafiken beschreiben",
      "Formelle E-Mails",
      "Passiv & Nominalisierung",
      "Redemittel für Diskussionen",
      "Prüfungstraining Sprechen",
      "Prüfungstraining Schreiben",
      "B2 Wiederholung",
    ],
    [
      "Verstehe anspruchsvollere Texte und erkenne zentrale Aussagen.",
      "Argumentiere klar, strukturiert und überzeugend.",
      "Trainiere professionelle Sprache für Arbeit und Beruf.",
      "Beschreibe Grafiken, Tabellen und Entwicklungen.",
      "Schreibe formelle E-Mails sicher und präzise.",
      "Nutze Passiv und Nominalstil für gehobenes Deutsch.",
      "Lerne starke Redemittel für Diskussionen und Meinungen.",
      "Trainiere B2-Sprechen mit klarer Struktur.",
      "Verbessere deinen Schreibstil für B2-Aufgaben.",
      "Wiederhole alle wichtigen B2-Themen mit System.",
    ],
    [
      "Hauptaussagen und Details in komplexeren Texten erkennen.",
      "Starke Argumente formulieren und Gegenargumente verstehen.",
      "Professionelle Gespräche und E-Mails sicher führen.",
      "Daten, Trends und Entwicklungen klar beschreiben.",
      "Formell, höflich und präzise schreiben.",
      "Gehobene Strukturen korrekt verwenden.",
      "Diskussionen mit passenden Redemitteln führen.",
      "Sprechaufgaben klar und souverän lösen.",
      "Texte strukturierter und genauer schreiben.",
      "B2-Inhalte sicher wiederholen.",
    ],
  ),
};

const getWhatsappHref = (number: string): string => {
  const digits = number.replace(/\D/g, "");
  return `https://wa.me/${digits}`;
};

const Content: React.FC<ContentProps> = ({
  courseLevel,
  course,
  studentName,
}) => {
  const [activeLessonIndex, setActiveLessonIndex] = useState<number>(0);

  const contentMainRef = useRef<HTMLElement | null>(null);
  const contentShellRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    document.documentElement.classList.add("content-screen-page");
    document.body.classList.add("content-screen-page");

    return () => {
      document.documentElement.classList.remove("content-screen-page");
      document.body.classList.remove("content-screen-page");
    };
  }, []);

  useEffect(() => {
    setActiveLessonIndex(0);
  }, [courseLevel]);

  const lessons = contentLessonsMap[courseLevel];
  const activeLesson = useMemo(
    () => lessons[activeLessonIndex] ?? lessons[0],
    [activeLessonIndex, lessons],
  );

  const progressPercent = Math.round(
    ((activeLessonIndex + 1) / lessons.length) * 100,
  );

  const nextLesson = lessons[activeLessonIndex + 1] ?? lessons[0];
  const whatsappHref = getWhatsappHref(course.whatsappNumber);

  const scrollToLessonTop = useCallback((): void => {
    window.requestAnimationFrame(() => {
      contentMainRef.current?.scrollTo({
        top: 0,
        behavior: "smooth",
      });

      contentShellRef.current?.scrollTo({
        top: 0,
        behavior: "smooth",
      });

      window.scrollTo({
        top: 0,
        behavior: "smooth",
      });
    });
  }, []);

  useEffect(() => {
    scrollToLessonTop();
  }, [activeLessonIndex, scrollToLessonTop]);

  const handleLessonSelect = (index: number): void => {
    setActiveLessonIndex(index);
  };

  const handleNextLesson = (): void => {
    setActiveLessonIndex((current) =>
      current < lessons.length - 1 ? current + 1 : 0,
    );
  };

  return (
    <div className="content-shell" ref={contentShellRef}>
      <div className="content-grid">
        <aside className="content-sidebar">
          <div className="content-sidebar-title">
            <div className="content-sidebar-icon">
              <FaLayerGroup />
            </div>

            <div>
              <h2>{courseLevel} Lernbereich</h2>
              <p>
                Willkommen{studentName ? `, ${studentName}` : ""} ·{" "}
                {lessons.length} Lektionen
              </p>
            </div>
          </div>

          <div className="content-progress-panel">
            <div className="content-progress-top">
              <span>Fortschritt</span>
              <strong>{progressPercent}%</strong>
            </div>

            <div className="content-progress-track">
              <div style={{ width: `${progressPercent}%` }} />
            </div>
          </div>

          <div className="content-lesson-list">
            {lessons.map((lesson, index) => (
              <button
                key={lesson.id}
                type="button"
                className={index === activeLessonIndex ? "active" : ""}
                onClick={() => handleLessonSelect(index)}
              >
                <span className="content-lesson-number">
                  {String(lesson.id).padStart(2, "0")}
                </span>

                <span className="content-lesson-text">
                  <strong>{lesson.title}</strong>
                  <small>
                    <FaClock />
                    {lesson.duration}
                  </small>
                </span>

                <span className="content-lesson-play">
                  {index <= activeLessonIndex ? <FaLockOpen /> : <FaPlay />}
                </span>
              </button>
            ))}
          </div>

          <div className="content-side-actions content-side-actions-single">
            <a href={whatsappHref} target="_blank" rel="noreferrer">
              <FaWhatsapp />
              Support
            </a>
          </div>
        </aside>

        <section className="content-main" ref={contentMainRef}>
          <div className="content-main-inner">
            <article className="content-video-card">
              <div className="content-video-top">
                <div>
                  <span>
                    <FaVideo />
                    Aktuelle Lektion
                  </span>
                  <h2>{activeLesson.title}</h2>
                  <p>{activeLesson.description}</p>
                </div>

                <div className="content-video-duration">
                  <FaClock />
                  {activeLesson.duration}
                </div>
              </div>

              <div className="content-video-frame">
                <video
                  key={activeLesson.videoUrl}
                  controls
                  playsInline
                  preload="metadata"
                  poster={activeLesson.posterUrl}
                >
                  <source src={activeLesson.videoUrl} type="video/mp4" />
                  Ihr Browser unterstützt dieses Video nicht.
                </video>

                <div className="content-video-watermark">
                  <FaCrown />
                  Premium Lernen
                </div>
              </div>
            </article>

            <section className="content-study-grid">
              <article className="content-study-card content-focus-card">
                <div className="content-study-icon">
                  <FaLightbulb />
                </div>

                <div>
                  <span>Lernziel</span>
                  <h3>Was du heute lernst</h3>
                  <p>{activeLesson.focus}</p>
                </div>
              </article>

              <article className="content-study-card">
                <div className="content-study-icon">
                  <FaHeadphones />
                </div>

                <div>
                  <span>Hören</span>
                  <h3>Video aktiv ansehen</h3>
                  <p>
                    Höre genau zu, stoppe das Video und wiederhole wichtige
                    Sätze laut.
                  </p>
                </div>
              </article>

              <article className="content-study-card">
                <div className="content-study-icon">
                  <FaMicrophoneAlt />
                </div>

                <div>
                  <span>Sprechen</span>
                  <h3>Laut trainieren</h3>
                  <p>
                    Sprich die Beispielsätze nach und bilde eigene kurze
                    Antworten.
                  </p>
                </div>
              </article>

              <article className="content-study-card">
                <div className="content-study-icon">
                  <FaPenNib />
                </div>

                <div>
                  <span>Schreiben</span>
                  <h3>Notizen machen</h3>
                  <p>
                    Schreibe neue Wörter und eigene Beispielsätze in dein Heft.
                  </p>
                </div>
              </article>
            </section>

            <section className="content-workspace">
              <article className="content-task-card">
                <div className="content-card-header">
                  <div>
                    <span>Übungsplan</span>
                    <h3>Schritt für Schritt lernen</h3>
                  </div>
                  <FaClipboardCheck />
                </div>

                <div className="content-check-list">
                  {activeLesson.practice.map((point) => (
                    <div key={point}>
                      <FaCheckCircle />
                      <span>{point}</span>
                    </div>
                  ))}
                </div>
              </article>

              <article className="content-task-card">
                <div className="content-card-header">
                  <div>
                    <span>Hausaufgabe</span>
                    <h3>Nach dem Video</h3>
                  </div>
                  <FaStar />
                </div>

                <div className="content-check-list">
                  {activeLesson.homework.map((point) => (
                    <div key={point}>
                      <FaCheckCircle />
                      <span>{point}</span>
                    </div>
                  ))}
                </div>
              </article>
            </section>

            <section className="content-next-card">
              <div>
                <span>Nächste Lektion</span>
                <h3>{nextLesson.title}</h3>
                <p>
                  Weiterlernen ohne Unterbrechung. Klicke auf „Weiter“, um zur
                  nächsten Lektion zu gehen.
                </p>
              </div>

              <button type="button" onClick={handleNextLesson}>
                Weiter
                <FaArrowRight />
              </button>
            </section>
          </div>
        </section>
      </div>
    </div>
  );
};
 
export default Content; 